import { createNanoEvents } from 'nanoevents'

import type { EventMapFromUnion } from '../types'

export interface AsyncMeta<T = unknown> {
  resolve: (value: T) => void
  reject: (reason?: unknown) => void
}

export function createTypedNanoEvents<TEventUnion extends { type: string }>() {
  type TMap = EventMapFromUnion<TEventUnion>

  const events = createNanoEvents<TMap>()

  const emit = <TType extends TEventUnion['type']>(
    type: TType,
    payload: Omit<Extract<TEventUnion, { type: TType }>, 'type'>,
  ) => {
    const event = { type, ...payload } as Extract<TEventUnion, { type: TType }>

    // @ts-expect-error nanoevents typing can't fully correlate `type` and payload here. Runtime is safe because `type` is restricted to the discriminated union.
    events.emit(type as keyof TMap, event as never)
  }

  const emitAsync = <TType extends TEventUnion['type']>(
    type: TType,
    payload: Omit<Extract<TEventUnion, { type: TType }>, 'type'>,
  ): Promise<unknown> => {
    let resolve!: (v: unknown) => void
    let reject!: (e: unknown) => void
    const promise = new Promise<unknown>((res, rej) => {
      resolve = res
      reject = rej
    })
    emit(type, { ...payload, meta: { resolve, reject } } as Omit<Extract<TEventUnion, { type: TType }>, 'type'>)
    return promise
  }

  return { events, emit, emitAsync }
}
